import { WEATHER_TILE_CHANNELS } from "@nova/constants"
import { Layer, project32 } from "@deck.gl/core"
import type { LayerContext, LayerProps, Viewport } from "@deck.gl/core"
import type { Framebuffer, Texture } from "@luma.gl/core"
import { Geometry, Model } from "@luma.gl/engine"
import { ColorStop, buildColormapGlsl } from "../shared/colormap"
import { FrameImage, FrameTextureCache } from "../shared/frameTextureCache"
import { HEATMAP_QUAD_POSITIONS, HEATMAP_QUAD_VERTEX_COUNT } from "../shared/heatmapGeometry"

export const WIND_HEATMAP_COLOR_STOPS: ReadonlyArray<ColorStop> = [
    { value: 0, color: [0.08, 0.12, 0.43] },
    { value: 3, color: [0.18, 0.42, 0.5] },
    { value: 5, color: [0.13, 0.65, 0.22] },
    { value: 8, color: [0.45, 0.62, 0.11] },
    { value: 11, color: [0.82, 0.7, 0.08] }, // golden yellow
    { value: 15, color: [0.88, 0.53, 0.04] }, // amber
    { value: 20, color: [0.75, 0.04, 0.63] },
    { value: 30, color: [0.36, 0.0, 0.6] },
]

export const WIND_HEATMAP_MAX_SPEED_MS =
    WIND_HEATMAP_COLOR_STOPS[WIND_HEATMAP_COLOR_STOPS.length - 1].value

const glslFloat = (v: number) => (Number.isInteger(v) ? v.toFixed(1) : String(v))

const { min: WIND_MIN_MS, max: WIND_MAX_MS } = WEATHER_TILE_CHANNELS.wind10m.u

const DECODE_WIND_GLSL = `
vec2 decodeWind(vec2 raw) {
    return raw * ${glslFloat(WIND_MAX_MS - WIND_MIN_MS)} + (${glslFloat(WIND_MIN_MS)});
}
`

const UPDATE_VS = `#version 300 es
in vec2 positions;
void main() {
    gl_Position = vec4(positions, 0.0, 1.0);
}
`

const UPDATE_FS = `#version 300 es
precision highp float;
precision highp sampler2D;

uniform sampler2D u_stateTexture;
// Bracketing frames lerped by u_windWeight — textures reused across ticks, only weight changes.
uniform sampler2D u_windTextureA;
uniform sampler2D u_windTextureB;
uniform float u_windWeight;
uniform vec2 u_pixelsPerNorm;
uniform float u_speedFactor;
uniform float u_deltaSeconds;
uniform float u_time;
uniform float u_maxAgeSeconds;
// Normalized-bbox rect the particles are confined to: the padded viewport, so on-screen
// density stays constant instead of falling off as the view shrinks against the data bbox.
uniform vec4 u_spawnBounds;
uniform float u_respawnAll;

out vec4 fragColor;

${DECODE_WIND_GLSL}

vec2 hash22(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.xx + p3.yz) * p3.zy);
}

// Catmull-Rom bicubic sampling (16 texels, C1 continuous). Nearest-filter is required —
// OES_texture_float_linear is optional in WebGL2. Server data row 0 = northern band;
// the y-flip in sampleNorm maps normPos.y=1 (north) to texture row 0.
float cubicWeight(float t) {
    float at = abs(t);
    float at2 = at * at;
    float at3 = at2 * at;
    return step(at, 1.0) * (1.5 * at3 - 2.5 * at2 + 1.0)
         + (1.0 - step(at, 1.0)) * step(at, 2.0) * (-0.5 * at3 + 2.5 * at2 - 4.0 * at + 2.0);
}

vec2 sampleWindBicubic(sampler2D tex, vec2 normPos) {
    vec2 dims = vec2(textureSize(tex, 0));
    vec2 sampleNorm = vec2(normPos.x, 1.0 - normPos.y);
    vec2 texPos = sampleNorm * (dims - 1.0);
    vec2 floorPos = floor(texPos);
    vec2 fracPos = texPos - floorPos;

    vec2 result = vec2(0.0);
    for (int j = -1; j <= 2; j++) {
        float wy = cubicWeight(float(j) - fracPos.y);
        for (int i = -1; i <= 2; i++) {
            vec2 uv = (floorPos + vec2(float(i), float(j)) + 0.5) / dims;
            uv = clamp(uv, 0.5 / dims, 1.0 - 0.5 / dims);
            result += cubicWeight(float(i) - fracPos.x) * wy * texture(tex, uv).rg;
        }
    }
    return result;
}

vec2 sampleWind(vec2 normPos) {
    return decodeWind(mix(
        sampleWindBicubic(u_windTextureA, normPos),
        sampleWindBicubic(u_windTextureB, normPos),
        u_windWeight
    ));
}

void main() {
    ivec2 dims = textureSize(u_stateTexture, 0);
    vec2 stateUv = gl_FragCoord.xy / vec2(dims);

    vec4 state = texture(u_stateTexture, stateUv);
    vec2 pos = state.xy;
    float age = state.z;

    vec2 wind = sampleWind(pos);

    // u_pixelsPerNorm = screen pixels spanned by the full data bbox on each axis at the
    // current zoom, so the per-second step is a constant number of *screen* pixels per m/s
    // (zoom-invariant on-screen speed).
    vec2 delta = vec2(
        wind.x * u_speedFactor * u_deltaSeconds / u_pixelsPerNorm.x,
        wind.y * u_speedFactor * u_deltaSeconds / u_pixelsPerNorm.y
    );

    vec2 newPos = pos + delta;
    float newAge = age + u_deltaSeconds;

    // A grown spawn rect leaves every particle inside it, so nothing recycles and the old,
    // smaller region stays clustered until it ages out — u_respawnAll redistributes instead.
    bool escaped = u_respawnAll > 0.5
        || any(lessThan(newPos, u_spawnBounds.xy))
        || any(greaterThan(newPos, u_spawnBounds.zw));
    if (newAge > u_maxAgeSeconds || escaped) {
        newPos = mix(u_spawnBounds.xy, u_spawnBounds.zw, hash22(stateUv + u_time));
        // A camera move escapes most of the set in one frame; staggering those ages keeps them
        // from then expiring in lockstep. Aging out keeps a full lifetime.
        newAge = escaped ? hash22(stateUv - u_time).x * u_maxAgeSeconds : 0.0;
    }

    fragColor = vec4(newPos, newAge, 1.0);
}
`

const HEATMAP_VS = `#version 300 es
in vec2 positions;
out vec2 v_normPos;

uniform vec4 u_bounds;

void main() {
    v_normPos = positions * 0.5 + 0.5;
    float lon = mix(u_bounds.x, u_bounds.z, v_normPos.x);
    float lat = mix(u_bounds.y, u_bounds.w, v_normPos.y);
    gl_Position = project_position_to_clipspace(vec3(lon, lat, 0.0), vec3(0.0), vec3(0.0));
}
`

const HEATMAP_FS = `#version 300 es
precision highp float;
precision highp sampler2D;

in vec2 v_normPos;
// Bracketing frames lerped by u_windWeight — textures reused across ticks, only weight changes.
uniform sampler2D u_windTextureA;
uniform sampler2D u_windTextureB;
uniform float u_windWeight;
uniform float u_opacity;
out vec4 fragColor;

${DECODE_WIND_GLSL}

float cubicWeight(float t) {
    float at = abs(t);
    float at2 = at * at;
    float at3 = at2 * at;
    return step(at, 1.0) * (1.5 * at3 - 2.5 * at2 + 1.0)
         + (1.0 - step(at, 1.0)) * step(at, 2.0) * (-0.5 * at3 + 2.5 * at2 - 4.0 * at + 2.0);
}

vec2 sampleWindBicubic(sampler2D tex, vec2 normPos) {
    vec2 dims = vec2(textureSize(tex, 0));
    vec2 sampleNorm = vec2(normPos.x, 1.0 - normPos.y);
    vec2 texPos = sampleNorm * (dims - 1.0);
    vec2 floorPos = floor(texPos);
    vec2 fracPos = texPos - floorPos;

    vec2 result = vec2(0.0);
    for (int j = -1; j <= 2; j++) {
        float wy = cubicWeight(float(j) - fracPos.y);
        for (int i = -1; i <= 2; i++) {
            vec2 uv = (floorPos + vec2(float(i), float(j)) + 0.5) / dims;
            uv = clamp(uv, 0.5 / dims, 1.0 - 0.5 / dims);
            result += cubicWeight(float(i) - fracPos.x) * wy * texture(tex, uv).rg;
        }
    }
    return result;
}

vec2 sampleWind(vec2 normPos) {
    return decodeWind(mix(
        sampleWindBicubic(u_windTextureA, normPos),
        sampleWindBicubic(u_windTextureB, normPos),
        u_windWeight
    ));
}

${buildColormapGlsl(WIND_HEATMAP_COLOR_STOPS, "windColormap")}

void main() {
    vec2 wind = sampleWind(v_normPos);
    float speed = length(wind);
    vec3 color = windColormap(speed);
    // Saturation boost — the ramp reads muted over the basemap; push each color away from
    // its luminance (grey) before applying opacity. 1.0 = unchanged, higher = more saturated.
    float luma = dot(color, vec3(0.299, 0.587, 0.114));
    color = clamp(mix(vec3(luma), color, 1.4), 0.0, 1.0);
    // Premultiplied so the scene FBO holds premultiplied color end-to-end (correct for the
    // additive trail add and the final over-blit).
    fragColor = vec4(color * u_opacity, u_opacity);
}
`

const DRAW_VS = `#version 300 es
precision highp float;
precision highp sampler2D;

in vec2 particleCoord;

uniform sampler2D u_stateTexture;
uniform sampler2D u_windTextureA;
uniform sampler2D u_windTextureB;
uniform float u_windWeight;
uniform vec4 u_bounds;
uniform float u_particleTexSize;
uniform float u_pointSize;

out float v_speed;

${DECODE_WIND_GLSL}

void main() {
    vec2 stateUv = (particleCoord + 0.5) / u_particleTexSize;
    vec4 state = texture(u_stateTexture, stateUv);
    vec2 normPos = state.xy;

    vec2 windA = decodeWind(texture(u_windTextureA, vec2(normPos.x, 1.0 - normPos.y)).rg);
    vec2 windB = decodeWind(texture(u_windTextureB, vec2(normPos.x, 1.0 - normPos.y)).rg);
    v_speed = length(mix(windA, windB, u_windWeight));

    float lon = mix(u_bounds.x, u_bounds.z, normPos.x);
    float lat = mix(u_bounds.y, u_bounds.w, normPos.y);
    gl_Position = project_position_to_clipspace(vec3(lon, lat, 0.0), vec3(0.0), vec3(0.0));
    gl_PointSize = u_pointSize;
}
`

const DRAW_FS = `#version 300 es
precision highp float;

uniform float u_headOpacity;
in float v_speed;
out vec4 fragColor;

${buildColormapGlsl(WIND_HEATMAP_COLOR_STOPS, "windColormap")}

void main() {
    // Round, soft-edged sprite so consecutive per-frame deposits blend into a smooth streak
    // rather than a chain of hard squares. Solid core to 60% radius, soft falloff to edge.
    vec2 d = gl_PointCoord - vec2(0.5);
    float dist = length(d) * 2.0;
    float coverage = u_headOpacity * smoothstep(1.0, 0.6, dist);

    // Color the deposit by the local wind speed (same ramp as the heatmap/legend), but pushed
    // strongly toward white so the additive trail stays brighter than the heatmap beneath and
    // reads clearly even in low-wind regions where the ramp colors are dark. Stored
    // premultiplied (rgb = color * coverage); the trail->scene pass adds it additively.
    vec3 color = mix(windColormap(v_speed), vec3(1.0), 0.35);
    fragColor = vec4(color * coverage, coverage);
}
`

const SCREEN_VS = `#version 300 es
in vec2 positions;
out vec2 v_uv;
void main() {
    v_uv = positions * 0.5 + 0.5;
    gl_Position = vec4(positions, 0.0, 1.0);
}
`

const FADE_FS = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_screenTexture;
uniform float u_fadeOpacity;
uniform float u_fadeBias;
out vec4 fragColor;
void main() {
    vec4 texel = texture(u_screenTexture, v_uv);
    fragColor = max(vec4(0.0), texel * u_fadeOpacity - vec4(u_fadeBias));
}
`

// Reads the heatmap (scene FBO) and trail buffer, adds them in-shader (true additive, not
// over-blend), then emits a premultiplied result. Doing the additive in the shader sidesteps
// luma not honoring a custom blend mode — no desaturated grey midtone, no dark fringe.
const COMPOSITE_FS = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_sceneTexture;
uniform sampler2D u_trailTexture;
uniform float u_trailGain;
out vec4 fragColor;
void main() {
    vec4 scene = texture(u_sceneTexture, v_uv);
    vec3 trail = texture(u_trailTexture, v_uv).rgb;
    fragColor = vec4(scene.rgb + trail * u_trailGain, scene.a);
}
`

type WindParticleLayerProps = LayerProps & {
    imageA: FrameImage
    imageB: FrameImage
    imageWeight: number
    bounds: [number, number, number, number]
    heatmapOpacity?: number
    numParticles?: number
    speedFactor?: number
    maxAgeSeconds?: number
    pointSize?: number
    headOpacity?: number
    fadeOpacity?: number
    trailGain?: number
    showParticles?: boolean
}

const DEFAULT_HEATMAP_OPACITY = 0.75
const DEFAULT_NUM_PARTICLES = 8000
const DEFAULT_SPEED_FACTOR = 4
const DEFAULT_MAX_AGE_SECONDS = 4
const DEFAULT_POINT_SIZE = 4
const DEFAULT_HEAD_OPACITY = 0.3
const DEFAULT_FADE_OPACITY = 0.99
const FADE_BIAS = 0.5 / 255
const DEFAULT_TRAIL_GAIN = 1.1
// Fraction of the view span the spawn rect extends past each edge, so trails flow in from
// offscreen instead of the upwind border showing a line of freshly born particles.
const SPAWN_PADDING = 0.15
const SPAWN_GROWTH_RESPAWN_RATIO = 1.3
const QUAD_POSITIONS = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1])

const getDevicePixelRatio = (): number => Math.max(1, globalThis.devicePixelRatio || 1)

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value))

const seedStateTextureData = (texSize: number, maxAgeSeconds: number): Float32Array => {
    const data = new Float32Array(texSize * texSize * 4)
    for (let i = 0; i < data.length; i += 4) {
        data[i] = Math.random()
        data[i + 1] = Math.random()
        data[i + 2] = Math.random() * maxAgeSeconds
        data[i + 3] = 0
    }
    return data
}

export class WindParticleLayer extends Layer<WindParticleLayerProps> {
    static layerName = "WindParticleLayer"
    static defaultProps = {
        imageWeight: { type: "number", value: 0, min: 0, max: 1 },
        bounds: { type: "array", value: [-180, -90, 180, 90], compare: true },
        heatmapOpacity: { type: "number", value: DEFAULT_HEATMAP_OPACITY, min: 0, max: 1 },
        numParticles: { type: "number", value: DEFAULT_NUM_PARTICLES, min: 1 },
        speedFactor: { type: "number", value: DEFAULT_SPEED_FACTOR, min: 0 },
        maxAgeSeconds: { type: "number", value: DEFAULT_MAX_AGE_SECONDS, min: 0 },
        pointSize: { type: "number", value: DEFAULT_POINT_SIZE, min: 0 },
        headOpacity: { type: "number", value: DEFAULT_HEAD_OPACITY, min: 0, max: 1 },
        fadeOpacity: { type: "number", value: DEFAULT_FADE_OPACITY, min: 0, max: 1 },
        trailGain: { type: "number", value: DEFAULT_TRAIL_GAIN, min: 0 },
        showParticles: { type: "boolean", value: true },
    }

    declare state: {
        // Particle sim ping-pong (rgba32float: xy = normalized pos, z = age).
        stateTextureA: Texture | null
        stateTextureB: Texture | null
        framebufferA: Framebuffer | null
        framebufferB: Framebuffer | null
        windTextureCache: FrameTextureCache
        updateModel: Model | null
        drawModel: Model | null
        heatmapModel: Model | null
        fadeModel: Model | null
        compositeModel: Model | null
        trailTextureA: Texture | null
        trailTextureB: Texture | null
        trailFramebufferA: Framebuffer | null
        trailFramebufferB: Framebuffer | null
        sceneTexture: Texture | null
        sceneFramebuffer: Framebuffer | null
        screenWidth: number
        screenHeight: number
        currentTrailIsA: boolean
        particleTexSize: number
        currentIsA: boolean
        // Smallest spawn-rect area the particles have been distributed over; growth past
        // SPAWN_GROWTH_RESPAWN_RATIO of it forces a redistribute.
        spawnAreaReference: number
        startTimeMs: number
        lastFrameTimeMs: number
        // Last frame's view-projection matrix — the trail buffer is screen-space, so on a
        // camera move its history no longer aligns with the world; we clear it then.
        lastViewProjectionMatrix: number[] | null
    }

    getModels() {
        const models = []
        if (this.state?.drawModel) models.push(this.state.drawModel)
        if (this.state?.heatmapModel) models.push(this.state.heatmapModel)
        return models
    }

    initializeState(context: LayerContext): void {
        const { device } = context
        const numParticles = this.props.numParticles ?? DEFAULT_NUM_PARTICLES
        const particleTexSize = Math.max(2, Math.ceil(Math.sqrt(numParticles)))
        const totalCells = particleTexSize * particleTexSize

        const seedData = seedStateTextureData(
            particleTexSize,
            this.props.maxAgeSeconds ?? DEFAULT_MAX_AGE_SECONDS
        )
        const stateTextureA = device.createTexture({
            width: particleTexSize,
            height: particleTexSize,
            format: "rgba32float",
            data: seedData,
            sampler: { magFilter: "nearest", minFilter: "nearest" },
        })
        const stateTextureB = device.createTexture({
            width: particleTexSize,
            height: particleTexSize,
            format: "rgba32float",
            data: seedData,
            sampler: { magFilter: "nearest", minFilter: "nearest" },
        })
        const framebufferA = device.createFramebuffer({ colorAttachments: [stateTextureA] })
        const framebufferB = device.createFramebuffer({ colorAttachments: [stateTextureB] })

        const windTextureCache = new FrameTextureCache()

        const updateModel = new Model(device, {
            id: "wind-update",
            vs: UPDATE_VS,
            fs: UPDATE_FS,
            geometry: new Geometry({
                topology: "triangle-strip",
                attributes: { positions: { size: 2, value: QUAD_POSITIONS } },
            }),
            vertexCount: 4,
        })

        // Heatmap quad — tessellated into latitude bands so the equirectangular texture stays
        // aligned to the Web Mercator basemap (see heatmapGeometry). project32 for the view
        // transform; premultiplied-over blend (into a cleared scene FBO this is just a copy).
        const heatmapModel = new Model(device, {
            id: "wind-heatmap",
            ...this.getShaders({ vs: HEATMAP_VS, fs: HEATMAP_FS, modules: [project32] }),
            geometry: new Geometry({
                topology: "triangle-strip",
                attributes: { positions: { size: 2, value: HEATMAP_QUAD_POSITIONS } },
            }),
            vertexCount: HEATMAP_QUAD_VERTEX_COUNT,
            parameters: {
                blendColorSrcFactor: "one",
                blendColorDstFactor: "one-minus-src-alpha",
                blendAlphaSrcFactor: "one",
                blendAlphaDstFactor: "one-minus-src-alpha",
            },
        })

        const particleCoords = new Float32Array(totalCells * 2)
        for (let j = 0; j < particleTexSize; j++) {
            for (let i = 0; i < particleTexSize; i++) {
                const k = (j * particleTexSize + i) * 2
                particleCoords[k] = i
                particleCoords[k + 1] = j
            }
        }
        const drawModel = new Model(device, {
            id: "wind-particle-draw",
            ...this.getShaders({ vs: DRAW_VS, fs: DRAW_FS, modules: [project32] }),
            geometry: new Geometry({
                topology: "point-list",
                attributes: { particleCoord: { size: 2, value: particleCoords } },
            }),
            // Premultiplied "over" into the trail buffer so overlapping deposits accumulate.
            parameters: {
                blendColorSrcFactor: "one",
                blendColorDstFactor: "one-minus-src-alpha",
                blendAlphaSrcFactor: "one",
                blendAlphaDstFactor: "one-minus-src-alpha",
            },
        })

        const fadeModel = new Model(device, {
            id: "wind-trail-fade",
            vs: SCREEN_VS,
            fs: FADE_FS,
            geometry: new Geometry({
                topology: "triangle-strip",
                attributes: { positions: { size: 2, value: QUAD_POSITIONS } },
            }),
            vertexCount: 4,
            parameters: {
                blendColorSrcFactor: "one",
                blendColorDstFactor: "one",
                blendAlphaSrcFactor: "one",
                blendAlphaDstFactor: "one",
            },
        })

        const compositeModel = new Model(device, {
            id: "wind-composite-blit",
            vs: SCREEN_VS,
            fs: COMPOSITE_FS,
            geometry: new Geometry({
                topology: "triangle-strip",
                attributes: { positions: { size: 2, value: QUAD_POSITIONS } },
            }),
            vertexCount: 4,
            parameters: {
                blendColorSrcFactor: "one",
                blendColorDstFactor: "one-minus-src-alpha",
                blendAlphaSrcFactor: "one",
                blendAlphaDstFactor: "one-minus-src-alpha",
            },
        })

        const dpr = getDevicePixelRatio()
        const screenWidth = Math.max(1, Math.round(context.viewport.width * dpr))
        const screenHeight = Math.max(1, Math.round(context.viewport.height * dpr))

        const makeScreenTexture = () =>
            device.createTexture({
                width: screenWidth,
                height: screenHeight,
                format: "rgba8unorm",
                sampler: { magFilter: "linear", minFilter: "linear" },
            })
        const trailTextureA = makeScreenTexture()
        const trailTextureB = makeScreenTexture()
        const sceneTexture = makeScreenTexture()
        const trailFramebufferA = device.createFramebuffer({ colorAttachments: [trailTextureA] })
        const trailFramebufferB = device.createFramebuffer({ colorAttachments: [trailTextureB] })
        const sceneFramebuffer = device.createFramebuffer({ colorAttachments: [sceneTexture] })

        const now = performance.now()
        this.setState({
            stateTextureA,
            stateTextureB,
            framebufferA,
            framebufferB,
            windTextureCache,
            updateModel,
            drawModel,
            heatmapModel,
            fadeModel,
            compositeModel,
            trailTextureA,
            trailTextureB,
            trailFramebufferA,
            trailFramebufferB,
            sceneTexture,
            sceneFramebuffer,
            screenWidth,
            screenHeight,
            currentTrailIsA: true,
            particleTexSize,
            currentIsA: true,
            spawnAreaReference: Infinity,
            startTimeMs: now,
            lastFrameTimeMs: now,
            lastViewProjectionMatrix: null,
        })
    }

    finalizeState(context: LayerContext): void {
        this.state.windTextureCache?.destroy()
        this.state.stateTextureA?.destroy()
        this.state.stateTextureB?.destroy()
        this.state.framebufferA?.destroy()
        this.state.framebufferB?.destroy()
        this.state.updateModel?.destroy()
        this.state.drawModel?.destroy()
        this.state.heatmapModel?.destroy()
        this.state.fadeModel?.destroy()
        this.state.compositeModel?.destroy()
        this.state.trailTextureA?.destroy()
        this.state.trailTextureB?.destroy()
        this.state.trailFramebufferA?.destroy()
        this.state.trailFramebufferB?.destroy()
        this.state.sceneTexture?.destroy()
        this.state.sceneFramebuffer?.destroy()
        super.finalizeState(context)
    }

    private ensureScreenSize(width: number, height: number): void {
        if (width === this.state.screenWidth && height === this.state.screenHeight) return
        this.state.trailFramebufferA?.resize([width, height])
        this.state.trailFramebufferB?.resize([width, height])
        this.state.sceneFramebuffer?.resize([width, height])
        this.setState({ screenWidth: width, screenHeight: height })
    }

    draw(): void {
        const {
            updateModel,
            drawModel,
            heatmapModel,
            fadeModel,
            compositeModel,
            windTextureCache,
            currentIsA,
            currentTrailIsA,
            stateTextureA,
            stateTextureB,
            framebufferA,
            framebufferB,
            trailTextureA,
            trailTextureB,
            trailFramebufferA,
            trailFramebufferB,
            sceneTexture,
            sceneFramebuffer,
            startTimeMs,
            lastFrameTimeMs,
            particleTexSize,
        } = this.state

        const { imageA, imageB } = this.props
        if (
            !updateModel ||
            !drawModel ||
            !heatmapModel ||
            !fadeModel ||
            !compositeModel ||
            !imageA ||
            imageA.width === 0 ||
            imageA.height === 0 ||
            !sceneTexture ||
            !sceneFramebuffer
        ) {
            return
        }

        const createWindTex = (image: FrameImage): Texture =>
            this.context.device.createTexture({
                width: image.width,
                height: image.height,
                format: "rgba8unorm",
                data: image.bitmap,
                sampler: { magFilter: "nearest", minFilter: "nearest" },
            })
        const windTextureA = windTextureCache.get(imageA, createWindTex)
        const windTextureB = windTextureCache.get(imageB, createWindTex)
        const windWeight = this.props.imageWeight ?? 0

        const readTexture = currentIsA ? stateTextureA : stateTextureB
        const writeFramebuffer = currentIsA ? framebufferB : framebufferA
        const writeTexture = currentIsA ? stateTextureB : stateTextureA
        if (!readTexture || !writeFramebuffer || !writeTexture) return

        const prevTrailTexture = currentTrailIsA ? trailTextureA : trailTextureB
        const currentTrailFb = currentTrailIsA ? trailFramebufferB : trailFramebufferA
        const currentTrailTexture = currentTrailIsA ? trailTextureB : trailTextureA
        if (!prevTrailTexture || !currentTrailFb || !currentTrailTexture) return

        const now = performance.now()
        const deltaSeconds = Math.min(0.1, (now - lastFrameTimeMs) / 1000)
        const elapsedSeconds = (now - startTimeMs) / 1000

        const { device, viewport } = this.context
        const dpr = getDevicePixelRatio()
        const screenW = Math.max(1, Math.round(viewport.width * dpr))
        const screenH = Math.max(1, Math.round(viewport.height * dpr))
        this.ensureScreenSize(screenW, screenH)

        const heatmapOpacity = this.props.heatmapOpacity ?? DEFAULT_HEATMAP_OPACITY
        const fadeOpacity = this.props.fadeOpacity ?? DEFAULT_FADE_OPACITY
        const pixelsPerNorm = this.computeBboxPixelSpan(viewport)
        const viewMatrix = viewport.viewProjectionMatrix
        const viewChanged = this.hasViewChanged(viewMatrix)
        const fullViewport: [number, number, number, number] = [0, 0, screenW, screenH]
        const showParticles = this.props.showParticles ?? true

        const spawnBounds = this.computeSpawnBounds(viewport)
        const spawnArea = (spawnBounds[2] - spawnBounds[0]) * (spawnBounds[3] - spawnBounds[1])
        const { spawnAreaReference } = this.state
        const respawnAll = spawnArea > spawnAreaReference * SPAWN_GROWTH_RESPAWN_RATIO

        if (showParticles) {
            updateModel.setBindings({
                u_stateTexture: readTexture,
                u_windTextureA: windTextureA,
                u_windTextureB: windTextureB,
            })
            updateModel.setUniforms({
                u_windWeight: windWeight,
                u_pixelsPerNorm: pixelsPerNorm,
                u_speedFactor: this.props.speedFactor ?? DEFAULT_SPEED_FACTOR,
                u_deltaSeconds: deltaSeconds,
                u_time: elapsedSeconds,
                u_maxAgeSeconds: this.props.maxAgeSeconds ?? DEFAULT_MAX_AGE_SECONDS,
                u_spawnBounds: spawnBounds,
                u_respawnAll: respawnAll ? 1 : 0,
            })
            const updatePass = device.beginRenderPass({
                framebuffer: writeFramebuffer,
                parameters: { viewport: [0, 0, particleTexSize, particleTexSize] },
            })
            updateModel.draw(updatePass)
            updatePass.end()
        }

        heatmapModel.setBindings({ u_windTextureA: windTextureA, u_windTextureB: windTextureB })
        heatmapModel.setUniforms({
            u_bounds: this.props.bounds,
            u_windWeight: windWeight,
            u_opacity: heatmapOpacity,
        })
        const heatmapPass = device.beginRenderPass({
            framebuffer: sceneFramebuffer,
            parameters: { viewport: fullViewport },
            clearColor: [0, 0, 0, 0],
        })
        heatmapModel.draw(heatmapPass)
        heatmapPass.end()

        if (showParticles) {
            const fadePass = device.beginRenderPass({
                framebuffer: currentTrailFb,
                parameters: { viewport: fullViewport },
                clearColor: [0, 0, 0, 0],
            })
            if (!viewChanged) {
                fadeModel.setBindings({ u_screenTexture: prevTrailTexture })
                fadeModel.setUniforms({ u_fadeOpacity: fadeOpacity, u_fadeBias: FADE_BIAS })
                fadeModel.draw(fadePass)
            }
            fadePass.end()

            drawModel.setBindings({
                u_stateTexture: writeTexture,
                u_windTextureA: windTextureA,
                u_windTextureB: windTextureB,
            })
            drawModel.setUniforms({
                u_bounds: this.props.bounds,
                u_windWeight: windWeight,
                u_particleTexSize: particleTexSize,
                u_pointSize: (this.props.pointSize ?? DEFAULT_POINT_SIZE) * dpr,
                u_headOpacity: this.props.headOpacity ?? DEFAULT_HEAD_OPACITY,
            })
            const particlePass = device.beginRenderPass({
                framebuffer: currentTrailFb,
                parameters: { viewport: fullViewport },
                clearColor: false,
            })
            drawModel.draw(particlePass)
            particlePass.end()
        }

        compositeModel.setBindings({
            u_sceneTexture: sceneTexture,
            u_trailTexture: currentTrailTexture,
        })
        compositeModel.setUniforms({
            u_trailGain: showParticles ? (this.props.trailGain ?? DEFAULT_TRAIL_GAIN) : 0,
        })
        compositeModel.draw(this.context.renderPass)

        this.setState({
            currentIsA: showParticles ? !currentIsA : currentIsA,
            currentTrailIsA: showParticles ? !currentTrailIsA : currentTrailIsA,
            spawnAreaReference: respawnAll ? spawnArea : Math.min(spawnAreaReference, spawnArea),
            lastFrameTimeMs: now,
            lastViewProjectionMatrix: Array.from(viewMatrix),
        })
        if (showParticles) this.setNeedsRedraw()
    }

    private hasViewChanged(viewMatrix: number[]): boolean {
        const previous = this.state.lastViewProjectionMatrix
        if (!previous) return false
        for (let i = 0; i < viewMatrix.length; i++) {
            if (Math.abs(viewMatrix[i] - previous[i]) > 1e-9) return true
        }
        return false
    }

    private computeSpawnBounds(viewport: Viewport) {
        const [west, south, east, north] = this.props.bounds
        const [viewWest, viewSouth, viewEast, viewNorth] = viewport.getBounds()
        const padLon = (viewEast - viewWest) * SPAWN_PADDING
        const padLat = (viewNorth - viewSouth) * SPAWN_PADDING
        const lonSpan = east - west
        const latSpan = north - south
        return [
            clamp01((viewWest - padLon - west) / lonSpan),
            clamp01((viewSouth - padLat - south) / latSpan),
            clamp01((viewEast + padLon - west) / lonSpan),
            clamp01((viewNorth + padLat - south) / latSpan),
        ] as [number, number, number, number]
    }

    private computeBboxPixelSpan(viewport: Viewport): [number, number] {
        const [west, south, east, north] = this.props.bounds
        const midLon = (west + east) / 2
        const midLat = (south + north) / 2
        const [westX] = viewport.project([west, midLat])
        const [eastX] = viewport.project([east, midLat])
        const [, southY] = viewport.project([midLon, south])
        const [, northY] = viewport.project([midLon, north])
        return [Math.max(1, Math.abs(eastX - westX)), Math.max(1, Math.abs(northY - southY))]
    }
}
